const RESPONSE_TYPES = {
  FETCH_XHR: "Fetch/XHR",
  WEBSOCKET: "WS",
  JAVASCRIPT: "JS",
  CSS: "CSS",
  MEDIA: "Media",
  FONT: "Font",
  DOCUMENT: "Doc",
  OTHER: "Other",
};

const DEFAULT_HEADERS = {
  request: {
    "Content-Type": "application/json",
    "User-Agent": "Mozilla/5.0",
    Accept: "*/*",
  },
  response: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Server: "TestServer",
  },
};

function formatSize(bytes) {
  if (bytes > 1024) {
    return `${(bytes / 1024).toFixed(1)} KiB`;
  }
  return `${bytes} B`;
}

function generateResponseTime(data) {
  if (data.duration) {
    return `${data.duration} ms`;
  }
  // Generate plausible random time between 10-1010 ms
  return `${Math.floor(Math.random() * 1000) + 10} ms`;
}

function determineResponseType(data) {
  if (data.type) {
    switch (data.type) {
      case "fetch":
      case "xhr":
        return RESPONSE_TYPES.FETCH_XHR;
      case "websocket":
        return RESPONSE_TYPES.WEBSOCKET;
      case "js":
        return RESPONSE_TYPES.JAVASCRIPT;
      case "css":
        return RESPONSE_TYPES.CSS;
      case "media":
        return RESPONSE_TYPES.MEDIA;
      case "font":
        return RESPONSE_TYPES.FONT;
      case "doc":
        return RESPONSE_TYPES.DOCUMENT;
      // If type is unknown, fall through to URL-based detection
    }
  }

  // URL-based detection
  const url = data.fullUrl;
  if (url.includes(".css")) return RESPONSE_TYPES.CSS;
  if (url.includes(".js")) return RESPONSE_TYPES.JAVASCRIPT;
  if (url.includes(".png") || url.includes(".jpg") || url.includes(".svg"))
    return RESPONSE_TYPES.MEDIA;
  if (url.includes(".woff") || url.includes(".ttf")) return RESPONSE_TYPES.FONT;
  if (url.includes(".html") || url.includes(".htm"))
    return RESPONSE_TYPES.DOCUMENT;
  if (url.includes("/api/") || data.method !== "GET")
    return RESPONSE_TYPES.FETCH_XHR;

  return RESPONSE_TYPES.OTHER;
}

function getContentType(responseType) {
  switch (responseType) {
    case RESPONSE_TYPES.MEDIA:
      return "image";
    case RESPONSE_TYPES.JAVASCRIPT:
      return "javascript";
    case RESPONSE_TYPES.CSS:
      return "css";
    case RESPONSE_TYPES.WEBSOCKET:
      return "websocket";
    default:
      return "json";
  }
}
const createNetworkRequestObj = (data) => {
  // Determine response type
  const responseType = determineResponseType(data);

  // Calculate size
  const size =
    typeof data.responseContent === "string" ? data.responseContent.length : 0;
  const sizeFormatted = formatSize(size);

  // Generate response time
  const time = generateResponseTime(data);

  // Process headers
  const headers = {
    request: {
      ...DEFAULT_HEADERS.request,
      ...((data.headers && data.headers.request) || {}),
    },
    response: {
      ...DEFAULT_HEADERS.response,
      ...((data.headers && data.headers.response) || {}),
    },
  };

  return {
    id: data.id,
    name: data.name,
    fullUrl: data.fullUrl,
    method: data.method,
    status: data.status,
    statusText:
      data.statusText ||
      (data.status >= 200 && data.status < 300 ? "OK" : "Error"),
    domain: data.domain,
    type: responseType,
    frame: 0,
    size: sizeFormatted,
    time,
    waterfall: "",
    responseContent: data.responseContent,
    timestamp: data.timestamp,
    headers,
    requestPayload: data.requestPayload,
    responseType: getContentType(responseType),
    rawType: data.type, // Store the original raw type
    error: data.error || false,
  };
};

// Export the main function
export default createNetworkRequestObj;
export const extractConsoleLogs = (event: any) => {
  if (typeof event.type === "string" && event.type.startsWith("console-")) {
    try {
      return {
        type: event.type.replace("console-", ""),
        data: event.data || "No data",
        timestamp: event.timestamp || Date.now(),
      };
    } catch (error) {
      console.error("Error processing console event:", error, event);
    }
  }
  return null;
};

export const extractUserAction = (event, index) => {
  const userActionTypes = [
    "click",
    "input",
    "keydown",
    "navigation",
    "submit",
    "keyboard-shortcut",
    "capslock-toggle",
    "modifier-press",
  ];

  if (typeof event.type === "string" && userActionTypes.includes(event.type)) {
    try {
      return {
        ...event,
        id: `action-${index}`, // Ensure each action has a unique ID
        timestamp: event.timestamp || Date.now(),
      };
    } catch (error) {
      console.error("Error processing user action event:", error, event);
    }
  }
  return null;
};

export const extractSystemInfoEvent = (event) => {
  if (typeof event.type === "string" && event.type === "system-info") {
    try {
      return {
        type: event.type.replace("system-", ""),
        os: event.os,
        browser: event.browser,
        windowSize: event.windowSize,
        country: event.country,
        url: event.url,
        timestamp: Date.now(),
        audioEnabled: event.audioEnabled || false,
      };
    } catch (error) {
      console.error("Error processing system info event:", error, event);
    }
  }
  return null;
};

export const extractAudioInfoEvent = (event) => {
  if (typeof event.type === "string" && event.type === "audio-info") {
    try {
      return { type: "audio-info", hasAudio: event.hasAudio };
    } catch (error) {
      console.error("Error processing audio info event:", error, event);
    }
  }
  return null;
};

export const extractUrlFromEvent = (event, t) => {
  if (event.url) {
    return event.url;
  } else if (
    event.request &&
    Array.isArray(event.request) &&
    event.request.length > 0
  ) {
    if (typeof event.request[0] === "string") {
      return event.request[0];
    } else if (event.request[0] && typeof event.request[0] === "object") {
      return event.request[0].url || t("Unknown_URL");
    }
  }
  return t("Unknown_URL");
};

export const extractMethodFromEvent = (event) => {
  if (event.method) {
    return event.method;
  } else if (event.request && event.request[1] && event.request[1].method) {
    return event.request[1].method;
  }
  return "GET";
};

export const tryParseUrl = (url) => {
  try {
    return new URL(url.startsWith("http") ? url : `https://example.com${url}`);
  } catch (e) {
    return {
      pathname: url,
      hostname: "unknown",
      protocol: "https:",
    };
  }
};

export const extractResponseContent = (response, t) => {
  try {
    if (typeof response === "string") {
      return response;
    } else if (response && typeof response === "object") {
      return JSON.stringify(response, null, 2);
    }
  } catch (e) {
    return t("Error_Parsing_Response");
  }
  return "";
};

export const extractRequestPayload = (event) => {
  if (event.request && event.request[1]) {
    if (event.request[1].body) {
      return event.request[1].body;
    } else if (event.request[1].data) {
      return event.request[1].data;
    }
  }
  return null;
};

export const extractFetchEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "fetch") {
    try {
      const url = extractUrlFromEvent(event, t);
      const method = extractMethodFromEvent(event);
      const status = event.status || 200;
      const statusText = event.statusText || "OK";
      const headers = event.headers || {};
      const duration = event.duration || 0;
      const urlObj = tryParseUrl(url);
      const responseContent = extractResponseContent(event.response, t);
      const requestPayload = extractRequestPayload(event);

      return createNetworkRequestObj({
        id: `network-${index}`,
        name: urlObj.pathname.split("/").pop() || urlObj.pathname,
        fullUrl: url,
        method,
        status,
        statusText,
        domain: urlObj.hostname,
        responseContent,
        timestamp: event.timestamp || Date.now(),
        requestPayload,
        type: "fetch",
        headers,
        duration,
      });
    } catch (error) {
      console.error("Error processing fetch event:", error, event);
    }
  }
  return null;
};

export const extractXhrEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "xhr") {
    try {
      const url = event.url || t("Unknown_URL");
      const method = event.method || "GET";
      const status = event.status || 200;
      const statusText = event.statusText || "OK";
      const duration = event.duration || 0;
      const urlObj = tryParseUrl(url);
      const responseContent = extractResponseContent(event.response, t);
      const requestPayload = event.requestData || null;

      const headers = {
        request: {},
        response: {},
      };

      if (event.headers) {
        if (event.headers.request) {
          headers.request = event.headers.request;
        }
        if (event.headers.response) {
          headers.response = event.headers.response;
        }
      }

      return createNetworkRequestObj({
        id: `network-${index}`,
        name: urlObj.pathname.split("/").pop() || urlObj.pathname,
        fullUrl: url,
        method,
        status,
        statusText,
        domain: urlObj.hostname,
        responseContent,
        timestamp: event.timestamp || Date.now(),
        requestPayload,
        type: "xhr",
        headers,
        duration,
      });
    } catch (error) {
      console.error("Error processing xhr event:", error, event);
    }
  }
  return null;
};

export const extractXhrErrorEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "xhr-error") {
    try {
      const url = event.url || t("Unknown_URL");
      const method = event.method || "GET";
      const status = event.status || 0;
      const urlObj = tryParseUrl(url);

      return createNetworkRequestObj({
        id: `network-${index}`,
        name: `Failed: ${urlObj.pathname.split("/").pop() || urlObj.pathname}`,
        fullUrl: url,
        method,
        status,
        statusText: t("Error"),
        domain: urlObj.hostname,
        responseContent: event.error || t("XHR_Request_Failed"),
        timestamp: event.timestamp || Date.now(),
        requestPayload: null,
        type: "xhr",
        headers: { request: {}, response: {} },
        duration: event.duration || 0,
        error: true,
      });
    } catch (error) {
      console.error("Error processing xhr-error event:", error, event);
    }
  }
  return null;
};

export const extractFetchErrorEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "fetch-error") {
    try {
      const url = event.url || t("Unknown_URL");
      const method = event.method || "GET";
      const urlObj = tryParseUrl(url);

      return createNetworkRequestObj({
        id: `network-${index}`,
        name: `Failed: ${urlObj.pathname.split("/").pop() || urlObj.pathname}`,
        fullUrl: url,
        method,
        status: 0,
        statusText: t("Error"),
        domain: urlObj.hostname,
        responseContent: event.error || t("Fetch_Request_Failed"),
        timestamp: event.timestamp || Date.now(),
        requestPayload: null,
        type: "fetch",
        headers: { request: {}, response: {} },
        duration: event.duration || 0,
        error: true,
      });
    } catch (error) {
      console.error("Error processing fetch-error event:", error, event);
    }
  }
  return null;
};

export const extractWebSocketEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type.startsWith("websocket-")) {
    try {
      const wsAction = event.type.replace("websocket-", "");
      const url = event.url || t("Unknown_WebSocket_URL");
      const urlObj = tryParseUrl(url);
      const responseContent = extractResponseContent(event.data, t);

      return createNetworkRequestObj({
        id: `network-${index}`,
        name: `WS ${wsAction} - ${
          urlObj.pathname.split("/").pop() || urlObj.pathname
        }`,
        fullUrl: url,
        method: wsAction.toUpperCase(), // Use WebSocket action as method
        status: wsAction === "error" ? 500 : 200, // Error status for WS errors
        statusText: wsAction === "error" ? t("Error") : "OK",
        domain: urlObj.hostname,
        responseContent: responseContent || `WebSocket ${wsAction} event`,
        timestamp: event.timestamp || Date.now(),
        requestPayload: event.data,
        type: "websocket",
        headers: { request: {}, response: {} },
        duration: 0,
        error: wsAction === "error",
      });
    } catch (error) {
      console.error("Error processing websocket event:", error, event);
    }
  }
  return null;
};

export const extractCssLoadEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "css-load") {
    try {
      return createNetworkRequestObj({
        id: index,
        name: event.href.split("/").pop() || "stylesheet",
        fullUrl: event.href,
        method: "GET",
        status: 200, // Assume success since it loaded
        statusText: "OK",
        domain: tryParseUrl(event.href).hostname,
        responseContent: t("CSS_Stylesheet"),
        timestamp: event.timestamp || Date.now(),
        requestPayload: null,
        type: "css", // Explicitly set type
        headers: {
          request: {},
          response: { "Content-Type": "text/css" },
        },
        duration: 0,
      });
    } catch (error) {
      console.error("Error processing CSS load event:", error, event);
    }
  }
  return null;
};

export const extractJsLoadEvent = (event, index) => {
  if (typeof event.type === "string" && event.type === "js-load") {
    try {
      return createNetworkRequestObj({
        id: index,
        name: event.src.split("/").pop() || "script",
        fullUrl: event.src,
        method: "GET",
        status: 200, // Assume success since it loaded
        statusText: "OK",
        domain: tryParseUrl(event.src).hostname,
        responseContent: "JavaScript",
        timestamp: event.timestamp || Date.now(),
        requestPayload: null,
        type: "js", // Explicitly set type
        headers: {
          request: {},
          response: { "Content-Type": "application/javascript" },
        },
        duration: 0,
      });
    } catch (error) {
      console.error("Error processing JS load event:", error, event);
    }
  }
  return null;
};

export const determineResourceType = (event, t) => {
  if (event.initiatorType === "css" || event.name.endsWith(".css")) {
    return "css";
  } else if (event.initiatorType === "script" || event.name.endsWith(".js")) {
    return "js";
  } else if (
    event.initiatorType === "img" ||
    ["png", "jpg", "jpeg", "gif", "svg", "webp"].some((ext) =>
      event.name.toLowerCase().endsWith("." + ext)
    )
  ) {
    return "media";
  } else if (
    event.initiatorType === "link" &&
    ["woff", "woff2", "ttf", "otf"].some((ext) =>
      event.name.toLowerCase().endsWith("." + ext)
    )
  ) {
    return "font";
  }
  return t("Other");
};

export const extractResourceLoadEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "resource-load") {
    try {
      const resourceType = determineResourceType(event, t);

      return createNetworkRequestObj({
        id: index,
        name: event.name.split("/").pop() || event.initiatorType,
        fullUrl: event.name,
        method: "GET",
        status: 200, // Assume success
        statusText: "OK",
        domain: tryParseUrl(event.name).hostname,
        responseContent: `${event.initiatorType} ${t("Resource")}`,
        timestamp: event.timestamp || Date.now(),
        requestPayload: null,
        type: resourceType,
        headers: { request: {}, response: {} },
        duration: event.duration || 0,
      });
    } catch (error) {
      console.error("Error processing resource load event:", error, event);
    }
  }
  return null;
};

export const determineDomResourceType = (event, t) => {
  if (event.tag === "link") {
    return "css";
  } else if (event.tag === "script") {
    return "js";
  } else if (event.tag === "img") {
    return "media";
  } else if (event.tag === "iframe") {
    return "doc";
  }
  return t("Other");
};

export const extractDomResourceLoadEvent = (event, index, t) => {
  if (typeof event.type === "string" && event.type === "dom-resource-load") {
    try {
      const resourceType = determineDomResourceType(event, t);

      return createNetworkRequestObj({
        id: index,
        name: event.src.split("/").pop() || event.tag,
        fullUrl: event.src,
        method: "GET",
        status: 200, // Assume success
        statusText: "OK",
        domain: tryParseUrl(event.src).hostname,
        responseContent: `${event.tag} ${t("Resource")}`,
        timestamp: event.timestamp || Date.now(),
        requestPayload: null,
        type: resourceType,
        headers: { request: {}, response: {} },
        duration: 0,
      });
    } catch (error) {
      console.error("Error processing DOM resource load event:", error, event);
    }
  }
  return null;
};

export const formatJSONWithSyntax = (obj) => {
  if (!obj) return "undefined";
  if (obj === null) return "null";
  if (typeof obj === "string") return `"${obj}"`;

  // For objects and arrays
  try {
    const json = JSON.stringify(obj, null, 2);
    return json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        function (match) {
          let cls = "color: #5f6368"; // default: gray
          if (/^"/.test(match)) {
            if (/:$/.test(match)) {
              cls = "color: #8bc34a"; // green for keys
            } else {
              cls = "color: #e91e63"; // red for strings
            }
          } else if (/true|false/.test(match)) {
            cls = "color: #ff9800"; // orange for booleans
          } else if (/null/.test(match)) {
            cls = "color: #9e9e9e"; // gray for null
          } else if (!isNaN(match as any)) {
            cls = "color: #2196f3"; // blue for numbers
          }
          return `<span style="${cls}">${match}</span>`;
        }
      );
  } catch (e) {
    return String(obj);
  }
};

export const formatTime = (timestamp) => {
  if (!timestamp) return "--:--";

  const date = new Date(timestamp);

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const getStatusColor = (status: number) => {
  if (status >= 200 && status < 300) return "#4caf50"; // Green for success
  if (status >= 300 && status < 400) return "#ff9800"; // Orange for redirect
  if (status >= 400) return "#f44336"; // Red for error
  return "#757575"; // Gray for unknown
};

export const getTypeIcon = (type: string) => {
  switch (type) {
    case "Fetch/XHR":
      return "🌐";
    case "WS":
      return "⚡";
    case "JS":
      return "⚙️";
    case "CSS":
      return "🎨";
    case "Media":
      return "🖼️";
    case "Font":
      return "🔤";
    case "Doc":
      return "📄";
    default:
      return "📦";
  }
};
